import { ROLES } from "./roles.js";
/**
 * Maps each route/resource to the roles that can access it.
 * If a resource is not listed here, it requires ADMIN by default.
 */
export const RESOURCE_PERMISSIONS = {
    // Contacts & Companies — all internal roles. Archive, restore, and
    // hard-delete are admin-only because they remove shared CRM records from
    // the normal working set.
    contacts: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "contacts:archive": [ROLES.ADMIN],
    "contacts:delete": [ROLES.ADMIN],
    "contacts:unarchive": [ROLES.ADMIN],
    companies: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "crm-deals:archive": [ROLES.ADMIN],
    "crm-deals:delete": [ROLES.ADMIN],
    "crm-deals:unarchive": [ROLES.ADMIN],
    "crm-follow-ups": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "crm-follow-ups:archive": [ROLES.ADMIN],
    "crm-follow-ups:delete": [ROLES.ADMIN],
    "crm-follow-ups:unarchive": [ROLES.ADMIN],
    "crm-campaigns": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "crm-campaigns:archive": [ROLES.ADMIN],
    "crm-campaigns:delete": [ROLES.ADMIN],
    "crm-campaigns:unarchive": [ROLES.ADMIN],
    // Contact Groups — same as contacts (admin/exec/sales/board)
    "contact-groups": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "contact-groups:delete": [ROLES.ADMIN],
    // Board Meetings — admin full access, board read-only (published+)
    "board-meetings": [ROLES.ADMIN, ROLES.BOARD],
    "board-meetings:write": [ROLES.ADMIN],
    "board-meetings:archive": [ROLES.ADMIN],
    // Document Vault — all internal roles (folder visibility handles fine-grained access)
    documents: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "documents:write": [ROLES.ADMIN, ROLES.EXEC],
    // Archive, hard-delete, and restore of documents are admin-only.
    "documents:archive": [ROLES.ADMIN],
    "documents:delete": [ROLES.ADMIN],
    "documents:unarchive": [ROLES.ADMIN],
    // E-Signatures — all internal roles
    signatures: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "signatures:delete": [ROLES.ADMIN],
    "signatures:expire": [ROLES.ADMIN],
    "signatures:revoke": [ROLES.ADMIN],
    // Messaging — all internal roles
    messages: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    // Chat compliance surfaces (DSAR, erasure, e-discovery) — admin only.
    // Phase G of chat-2026-05.md.
    "messages:compliance": [ROLES.ADMIN],
    // Meeting recordings — internal capture/transcript store.
    meetings: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "meetings:archive": [ROLES.ADMIN],
    "meetings:delete": [ROLES.ADMIN],
    "meetings:unarchive": [ROLES.ADMIN],
    // Cashflow & QBO — admin and exec
    cashflow: [ROLES.ADMIN, ROLES.EXEC],
    "cashflow:approve": [ROLES.ADMIN, ROLES.EXEC],
    "cashflow:delete": [ROLES.ADMIN],
    quickbooks: [ROLES.ADMIN, ROLES.EXEC],
    // Receipt capture — sales can capture/upload; approval into cashflow
    // stays with management.
    receipts: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "receipts:approve": [ROLES.ADMIN, ROLES.EXEC],
    // Tracked Email — all internal roles
    email: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "email:archive": [ROLES.ADMIN],
    "email:bounce-sync": [ROLES.ADMIN],
    // Email auto-capture cron — admin/cron only (X-Cron-Secret sets role=admin).
    "email:capture-sync": [ROLES.ADMIN],
    "email:delete": [ROLES.ADMIN],
    "email:unarchive": [ROLES.ADMIN],
    // Authenticator — internal shared TOTP vault. Board users can receive
    // shared codes for board/vendor access, but client portal users cannot.
    authenticator: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    // Sharing — link creation is staff-facing; lifecycle and audit controls are admin-only.
    sharing: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "sharing:audit": [ROLES.ADMIN],
    "sharing:delete": [ROLES.ADMIN],
    "sharing:revoke": [ROLES.ADMIN],
    // Tenant admin — admin only
    tenants: [ROLES.ADMIN],
    // Partner vaults console (ADR-0106) — admin only, and deliberately NOT
    // ADMIN/EXEC like the rest of the workspace surface. This screen reaches
    // across every partner tenant at once and can archive a whole workstream,
    // so it is a narrower grant than editing inside one partner.
    "partner-vaults": [ROLES.ADMIN],
    // AI features — all internal roles
    ai: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    // Feature flags — all internal roles can read their evaluated flags;
    // server-side configuration remains environment/operator controlled.
    "feature-flags": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    // Help — everyone including clients
    help: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD, ROLES.CLIENT],
    // Projects — admin/exec/sales (Board excluded per RBAC matrix in
    // docs/design-canon/02-api/rbac.md). Archive, hard-delete, and restore
    // are admin-only.
    projects: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "projects:archive": [ROLES.ADMIN],
    "projects:delete": [ROLES.ADMIN],
    "projects:unarchive": [ROLES.ADMIN],
    // Tasks — same as projects for the per-project sub-routes. Cross-user
    // task views (/by-assignee/:email) lock to ADMIN+EXEC only — see rbac.md
    // R5 (cross-user data views default to management roles).
    tasks: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "tasks:cross-user": [ROLES.ADMIN, ROLES.EXEC],
    "tasks:delete": [ROLES.ADMIN],
    // Companies — same shape.
    "companies:archive": [ROLES.ADMIN],
    "companies:delete": [ROLES.ADMIN],
    "companies:unarchive": [ROLES.ADMIN],
    // Board meetings — same shape. Board users can read published board
    // materials; admin controls lifecycle changes.
    "board-meetings:delete": [ROLES.ADMIN],
    "board-meetings:unarchive": [ROLES.ADMIN],
    // Vault folders — admin/exec already manage folders; admin-only for
    // archive, hard-delete, and restore.
    "documents:folder-archive": [ROLES.ADMIN],
    "documents:folder-delete": [ROLES.ADMIN],
    "documents:folder-unarchive": [ROLES.ADMIN],
    // Portal admin — L30 staff manage external (magic-link) portal
    // users for any tenant. The customer-facing portal routes
    // (`/api/portal/*`, `/api/auth/magic-link/*`) DO NOT use the
    // RBAC middleware; they authenticate via the magic-link session
    // cookie set in PR #?? rather than the Clerk path.
    "portal-admin": [ROLES.ADMIN, ROLES.EXEC],
    "portal-admin:provision": [ROLES.ADMIN],
    "portal-admin:archive": [ROLES.ADMIN],
    "portal-admin:delete": [ROLES.ADMIN],
    // Tenant registry — read-only listing for the cross-tenant switcher
    // (e.g. picking which tenant's portal users to manage). Returns
    // slugs only, no per-tenant data. Distinct from the broader
    // `tenants` write permission above.
    "tenants:list": [ROLES.ADMIN, ROLES.EXEC],
    // Notes — internal markdown workspace, available to every internal
    // role (matches the rest of the CRM). Archive is admin-only as a
    // soft-delete safety net.
    notes: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "notes:archive": [ROLES.ADMIN],
    "notes:delete": [ROLES.ADMIN],
    // Ideation — premium graph/read-model over the notes workspace.
    // It shares the internal-only posture of notes. Saved-board lifecycle
    // archive/restore/delete is admin-only because boards are shared staff
    // workspace objects.
    ideation: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "ideation:approve": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES],
    "ideation:archive": [ROLES.ADMIN],
    "ideation:delete": [ROLES.ADMIN],
    "ideation:unarchive": [ROLES.ADMIN],
    // Scheduling (Calendly-equivalent feature #3) — every internal role
    // owns their own event types + availability. Cross-user views
    // ("scheduling:admin", e.g. delete someone else's event type during
    // offboarding) are admin-only.
    scheduling: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "scheduling:admin": [ROLES.ADMIN],
    // Time tracking (feature #5) — every internal role logs their own
    // hours. Cross-user views ("time-tracking:admin") for admin/exec
    // who run timesheet rollups for invoicing.
    "time-tracking": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    "time-tracking:admin": [ROLES.ADMIN, ROLES.EXEC],
    // Personal Access Tokens — every internal (Clerk) role manages their
    // OWN tokens; the service layer scopes all reads/writes to the caller's
    // Clerk user id, so there is no cross-user exposure and no admin view in
    // v1. Clients use the magic-link portal (no Clerk session), so they never
    // reach this router. Token creation is Clerk-only — a PAT can't mint a PAT
    // (enforced in the router, not here). See
    // docs/enhancements/ideation-capture-2026-05.md §A.5.
    tokens: [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    // Per-user UI preferences (sidebar favourites/collapse). Every staff
    // role manages only their OWN prefs (identity from auth context); no
    // cross-user surface, so all internal roles are allowed.
    "ui-preferences": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
    // Team members — read-only directory of the tenant's staff (name + email),
    // powering people pickers (task Lead/Support, later email composer + share
    // modals). Every internal role may read it: staff are colleagues who already
    // see each other, and name+email is not sensitive within the team boundary.
    // Clients (magic-link portal users) are excluded — they never reach the
    // Clerk-authenticated routes and have no business browsing the staff list.
    "team-members": [ROLES.ADMIN, ROLES.EXEC, ROLES.SALES, ROLES.BOARD],
};
/**
 * Whether `role` may access `resource` per RESOURCE_PERMISSIONS.
 * Mirrors API `requireRole`: unknown resources default to admin-only.
 */
export function roleCanAccess(resource, role) {
    const allowed = RESOURCE_PERMISSIONS[resource];
    if (!allowed)
        return role === ROLES.ADMIN;
    return allowed.includes(role);
}
//# sourceMappingURL=permissions.js.map